import {v4 as uuidv4} from 'uuid';
import {STARTER_CHARACTERS, createStarterCostumes} from './starterArt.ts';

// Versioned document stored in ProjectSceneScriptAndHtml.SceneScript for
// Blocks tabs. Stage uses Scratch coordinates: x -240..240, y -180..180.
export const BLOCKS_FORMAT = 'mixiply-blocks';
// Keep in step with BlocksDocument.CurrentVersion and BlocksDocumentMigrator
// on the server; migrateProject() below mirrors its steps.
export const BLOCKS_VERSION = 3;
export const STAGE_WIDTH = 480;
export const STAGE_HEIGHT = 360;

export interface BlocksCostume {
    id: string;
    name: string;
    // A video costume plays its mp4 (see the Video blocks). A text costume
    // draws its words instead of a picture (a label, a score) and has no url.
    type: 'image' | 'spriteSheet' | 'video' | 'text';
    url: string;
    text?: string;
    // Text costumes: size in pixels (24), colour (#2c2340), font family.
    size?: number;
    color?: string;
    font?: 'sans' | 'serif' | 'mono';
    frameWidth?: number;
    frameHeight?: number;
    frameIndex?: number;
    // Display size for image costumes; unset means natural image size.
    // Set automatically when a large image is imported (see imageFit.ts).
    width?: number;
    height?: number;
    // The *.sprite.json this costume was drawn from, when it came from the
    // sprite editor — enables reopening the drawing for editing.
    sourceSpriteUrl?: string;
}

export interface BlocksSound {
    id: string;
    name: string;
    url: string;
}

export interface BlocksBackdrop {
    id: string;
    name: string;
    type: 'color' | 'image' | 'video';
    value?: string;
    url?: string;
}

export interface BlocksSprite {
    id: string;
    name: string;
    x: number;
    y: number;
    direction: number;
    size: number;
    visible: boolean;
    currentCostume: number;
    costumes: BlocksCostume[];
    sounds: BlocksSound[];
    // Blockly.serialization.workspaces.save() output for this sprite's scripts.
    workspace: object | null;
    // In every scene (a narrator, a score) rather than only the scenes that list it.
    global?: boolean;
    // The sprite's own variables ("for this sprite only"): each clone starts
    // with a copy of the original's values. Listed in the workspace too, by
    // the same ids, so Blockly offers them.
    variables?: BlocksVariable[];
}

// Project variables are the declared globals and their starting values.
// Workspaces also list the variables their blocks use (Blockly needs them);
// at runtime a variable is the sprite's own if the sprite declares it,
// otherwise global by name.
export interface BlocksVariable {
    id: string;
    name: string;
    value: number | string;
}

export interface BlocksStage {
    width: number;
    height: number;
    backdrops: BlocksBackdrop[];
    currentBackdrop: number;
    // The stage's own scripts ("when stage clicked", music) and sounds.
    workspace?: object | null;
    sounds?: BlocksSound[];
    // Start by itself when someone opens the project.
    autoStart?: boolean;
    // On-screen buttons for phones that press the arrow keys (and space).
    controls?: BlocksControls;
}

export type BlocksControls = 'none' | 'arrows' | 'arrows-space';

// A place in the project: the backdrop it shows and the sprites in it.
// Global sprites are in every scene. Backdrops are named from the stage's list.
export interface BlocksScene {
    id: string;
    name: string;
    backdrop?: string | null;
    spriteIds: string[];
}

// The stage is selected in the editor like a sprite, under this id.
export const STAGE_TARGET_ID = '__stage__';

// A list shared by every sprite, with the items it starts with.
export interface BlocksList {
    id: string;
    name: string;
    value: (number | string)[];
}

export interface BlocksProject {
    format: typeof BLOCKS_FORMAT;
    version: number;
    stage: BlocksStage;
    sprites: BlocksSprite[];
    variables: BlocksVariable[];
    // Broadcast message names (see the Events "broadcast" blocks).
    messages: string[];
    lists?: BlocksList[];
    // No scenes means one place with every sprite.
    scenes?: BlocksScene[];
    // The scene the green flag starts in; the first scene when unset.
    startScene?: string;
}

export const hasScenes = (project: BlocksProject) =>
    (project.scenes?.length ?? 0) > 0;

export function startSceneOf(project: BlocksProject): BlocksScene | undefined {
    const scenes = project.scenes ?? [];
    return scenes.find(scene => scene.name === project.startScene) ?? scenes[0];
}

// The sprites someone sees in a scene: its own plus the global ones.
export function spritesInScene(
    project: BlocksProject,
    scene: BlocksScene | undefined
): BlocksSprite[] {
    if (!scene) {
        return project.sprites;
    }
    return project.sprites.filter(
        sprite => sprite.global || scene.spriteIds.includes(sprite.id)
    );
}

// A name not used yet in the list: "Scene 3", "Scene 4"…
export function nextName(prefix: string, taken: string[]): string {
    let index = taken.length + 1;
    while (taken.includes(`${prefix} ${index}`)) {
        index++;
    }
    return `${prefix} ${index}`;
}

/*
 * Adds a scene. The first time, the project as it stands becomes "Scene 1"
 * (every sprite, the current backdrop) so nothing disappears, and the new
 * scene comes after it. Each new scene gets a backdrop of its own.
 */
export function addScene(project: BlocksProject): {
    project: BlocksProject;
    scene: BlocksScene;
} {
    const scenes = [...(project.scenes ?? [])];
    let backdrops = project.stage.backdrops;

    if (scenes.length === 0) {
        scenes.push({
            id: uuidv4(),
            name: 'Scene 1',
            backdrop: backdrops[project.stage.currentBackdrop]?.name ?? null,
            spriteIds: project.sprites.map(sprite => sprite.id)
        });
    }

    const name = nextName(
        'Scene',
        scenes.map(scene => scene.name)
    );
    const backdropName = backdrops.some(b => b.name === name)
        ? nextName('backdrop', backdrops.map(b => b.name))
        : name;
    backdrops = [
        ...backdrops,
        {id: uuidv4(), name: backdropName, type: 'color', value: '#ffffff'}
    ];
    const scene: BlocksScene = {
        id: uuidv4(),
        name,
        backdrop: backdropName,
        spriteIds: []
    };

    return {
        project: {
            ...project,
            stage: {...project.stage, backdrops},
            scenes: [...scenes, scene]
        },
        scene
    };
}

export type BlocksShapeKind =
    | 'circle'
    | 'square'
    | 'triangle'
    | 'star'
    | 'diamond'
    | 'heart'
    | 'ring';

export const BLOCKS_SHAPES: {kind: BlocksShapeKind; label: string}[] = [
    {kind: 'circle', label: 'Circle'},
    {kind: 'square', label: 'Square'},
    {kind: 'triangle', label: 'Triangle'},
    {kind: 'star', label: 'Star'},
    {kind: 'diamond', label: 'Diamond'},
    {kind: 'heart', label: 'Heart'},
    {kind: 'ring', label: 'Ring'}
];

// Scratch-ish swatches for the shape picker.
export const SHAPE_PALETTE = [
    '#4c97ff',
    '#5cb1d6',
    '#0fbd8c',
    '#59c059',
    '#ffbf00',
    '#ff8c1a',
    '#d0342c',
    '#cf63cf',
    '#9966ff',
    '#2c2340'
];

export function shapeCostumeUrl(
    shape: BlocksShapeKind,
    color: string
): string {
    let body: string;
    switch (shape) {
        case 'circle':
            body = `<circle cx="30" cy="30" r="28" fill="${color}"/>`;
            break;
        case 'triangle':
            body = `<polygon points="30,3 57,57 3,57" fill="${color}"/>`;
            break;
        case 'star':
            body = `<polygon points="30,2 36.5,21.1 56.6,21.3 40.5,33.4 46.5,52.7 30,41 13.5,52.7 19.5,33.4 3.4,21.3 23.5,21.1" fill="${color}"/>`;
            break;
        case 'diamond':
            body = `<polygon points="30,3 57,30 30,57 3,30" fill="${color}"/>`;
            break;
        case 'heart':
            body = `<path d="M30,52 C14,40 5,27 9,16 C12,8 24,6 30,16 C36,6 48,8 51,16 C55,27 46,40 30,52 Z" fill="${color}"/>`;
            break;
        case 'ring':
            body = `<circle cx="30" cy="30" r="23" fill="none" stroke="${color}" stroke-width="10"/>`;
            break;
        default:
            body = `<rect x="3" y="3" width="54" height="54" rx="8" fill="${color}"/>`;
    }

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">${body}</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export function createShapeCostume(
    name: string,
    shape: BlocksShapeKind,
    color: string
): BlocksCostume {
    return {
        id: uuidv4(),
        name,
        type: 'image',
        url: shapeCostumeUrl(shape, color)
    };
}

export function createImageCostume(name: string, url: string): BlocksCostume {
    return {id: uuidv4(), name, type: 'image', url};
}

export function createTextCostume(name: string, text: string): BlocksCostume {
    return {id: uuidv4(), name, type: 'text', url: '', text};
}

export function createSprite(
    name: string,
    costume: BlocksCostume
): BlocksSprite {
    return {
        id: uuidv4(),
        name,
        x: 0,
        y: 0,
        direction: 90,
        size: 100,
        visible: true,
        currentCostume: 0,
        costumes: [costume],
        sounds: [],
        workspace: null
    };
}

// A tiny example stack so a brand-new Blocks tab does something on Run.
// Hand-written Blockly.serialization shape (same as the toolbox shadows).
function starterWorkspace(): object {
    return {
        blocks: {
            languageVersion: 0,
            blocks: [
                {
                    type: 'event_whenflag',
                    x: 40,
                    y: 40,
                    next: {
                        block: {
                            type: 'looks_sayfor',
                            inputs: {
                                TEXT: {
                                    shadow: {
                                        type: 'text',
                                        fields: {TEXT: 'Hello!'}
                                    }
                                },
                                SECS: {
                                    shadow: {
                                        type: 'math_number',
                                        fields: {NUM: 2}
                                    }
                                }
                            },
                            next: {
                                block: {
                                    type: 'motion_move',
                                    inputs: {
                                        STEPS: {
                                            shadow: {
                                                type: 'math_number',
                                                fields: {NUM: 10}
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            ]
        }
    };
}

export function createDefaultProject(): BlocksProject {
    // New projects start with the Mixiply duck (falling back to a plain
    // circle if the starter art ever goes missing).
    const duck = STARTER_CHARACTERS.find(
        character => character.key === 'duck'
    );
    const costumes = duck
        ? createStarterCostumes(duck)
        : [createShapeCostume('costume1', 'circle', '#4c97ff')];

    const sprite = {
        ...createSprite(duck?.label ?? 'Sprite1', costumes[0]),
        costumes
    };
    sprite.workspace = starterWorkspace();

    return {
        format: BLOCKS_FORMAT,
        version: BLOCKS_VERSION,
        stage: {
            width: STAGE_WIDTH,
            height: STAGE_HEIGHT,
            backdrops: [
                {id: uuidv4(), name: 'plain', type: 'color', value: '#ffffff'}
            ],
            currentBackdrop: 0
        },
        sprites: [sprite],
        variables: [],
        messages: ['message1']
    };
}

export class UnsupportedBlocksVersionError extends Error {
    constructor(version: number) {
        super(
            `This project was saved by a newer version of Mixiply (format ${version}). Reload the page to edit it.`
        );
    }
}

interface WorkspaceVariables {
    variables?: {id?: string; name?: string}[];
}

/*
 * Upgrades an older document in place, one version at a time. Mirrors
 * BlocksDocumentMigrator on the server: change both together. The server
 * migrates on read too; this covers documents the editor builds itself.
 *
 * 1 -> 2: messages and each sprite's sounds become required, and project
 * variables become the declared globals (the union by name of every
 * workspace's variables, starting at 0).
 *
 * 2 -> 3: the costume, backdrop, message and clone-target dropdowns of switch
 * costume, switch backdrop (and wait), broadcast (and wait) and create clone
 * become value inputs with a menu block as their shadow, so a reporter can
 * take their place: fields.COSTUME moves to inputs.COSTUME.shadow.
 */
const MENU_INPUTS: Record<string, {field: string; menu: string}> = {
    looks_switchcostume: {field: 'COSTUME', menu: 'menu_costume'},
    looks_switchbackdrop: {field: 'BACKDROP', menu: 'menu_backdrop'},
    looks_switchbackdropandwait: {field: 'BACKDROP', menu: 'menu_backdrop'},
    event_broadcast: {field: 'MSG', menu: 'menu_message'},
    event_broadcastandwait: {field: 'MSG', menu: 'menu_message'},
    control_createclone: {field: 'CLONE_OF', menu: 'menu_clonetarget'}
};

interface SerializedBlock {
    type?: string;
    fields?: Record<string, unknown>;
    inputs?: Record<string, {block?: SerializedBlock; shadow?: SerializedBlock}>;
    next?: {block?: SerializedBlock};
}

function menuFieldsToInputs(block: SerializedBlock | undefined): void {
    if (!block) {
        return;
    }
    const menu = block.type ? MENU_INPUTS[block.type] : undefined;
    if (menu && block.fields && menu.field in block.fields) {
        const value = block.fields[menu.field];
        delete block.fields[menu.field];
        if (Object.keys(block.fields).length === 0) {
            delete block.fields;
        }
        block.inputs ??= {};
        block.inputs[menu.field] = {
            ...block.inputs[menu.field],
            shadow: {type: menu.menu, fields: {[menu.field]: value}}
        };
    }
    for (const slot of Object.values(block.inputs ?? {})) {
        menuFieldsToInputs(slot.block);
        menuFieldsToInputs(slot.shadow);
    }
    menuFieldsToInputs(block.next?.block);
}

export function migrateProject(project: BlocksProject): BlocksProject {
    const version = Number(project.version) || 1;

    if (version > BLOCKS_VERSION) {
        throw new UnsupportedBlocksVersionError(version);
    }

    if (version < 2) {
        project.messages ??= [];
        project.variables ??= [];
        const declared = new Set(project.variables.map(v => v.name));

        for (const sprite of project.sprites ?? []) {
            sprite.sounds ??= [];
            sprite.costumes ??= [];
            const workspace = sprite.workspace as WorkspaceVariables | null;
            for (const variable of workspace?.variables ?? []) {
                if (variable.name && !declared.has(variable.name)) {
                    declared.add(variable.name);
                    project.variables.push({
                        id: variable.id ?? variable.name,
                        name: variable.name,
                        value: 0
                    });
                }
            }
        }

        project.version = 2;
    }

    if (version < 3) {
        const targets = [...(project.sprites ?? []), project.stage];
        for (const target of targets) {
            const workspace = target?.workspace as
                | {blocks?: {blocks?: SerializedBlock[]}}
                | null
                | undefined;
            for (const top of workspace?.blocks?.blocks ?? []) {
                menuFieldsToInputs(top);
            }
        }
        project.version = 3;
    }

    return project;
}

// Adds any variable a workspace uses that the project doesn't declare yet, so
// project.variables stays the full list of globals as people edit. A sprite's
// own variables are left to the sprite.
export function withDeclaredVariables(project: BlocksProject): BlocksProject {
    const declared = new Set(project.variables.map(v => v.name));
    const added: BlocksVariable[] = [];

    for (const sprite of project.sprites) {
        const workspace = sprite.workspace as WorkspaceVariables | null;
        const locals = new Set((sprite.variables ?? []).map(v => v.name));
        for (const variable of workspace?.variables ?? []) {
            if (variable.name && !declared.has(variable.name) && !locals.has(variable.name)) {
                declared.add(variable.name);
                added.push({
                    id: variable.id ?? variable.name,
                    name: variable.name,
                    value: 0
                });
            }
        }
    }

    return added.length > 0
        ? {...project, variables: [...project.variables, ...added]}
        : project;
}

// Keeps a sprite's own variables in step with its workspace after an edit:
// one renamed in Blockly is renamed here, one deleted in Blockly goes.
export function withSyncedLocalVariables(sprite: BlocksSprite): BlocksSprite {
    if (!sprite.variables || sprite.variables.length === 0) {
        return sprite;
    }
    const listed = new Map(
        ((sprite.workspace as WorkspaceVariables | null)?.variables ?? [])
            .filter(v => v.id && v.name)
            .map(v => [v.id!, v.name!])
    );
    const variables = sprite.variables
        .filter(v => listed.has(v.id))
        .map(v => (listed.get(v.id) === v.name ? v : {...v, name: listed.get(v.id)!}));
    const unchanged =
        variables.length === sprite.variables.length &&
        variables.every((v, i) => v === sprite.variables![i]);
    return unchanged ? sprite : {...sprite, variables};
}

// A document from the server (or an old SceneScript string); anything that
// isn't a Blocks document falls back to a new default project.
export function parseBlocksProject(
    source: string | object | null | undefined
): BlocksProject {
    let parsed: unknown = source;

    if (typeof source === 'string') {
        try {
            parsed = JSON.parse(source);
        } catch {
            parsed = null;
        }
    }

    if (
        parsed &&
        typeof parsed === 'object' &&
        (parsed as BlocksProject).format === BLOCKS_FORMAT
    ) {
        return migrateProject(parsed as BlocksProject);
    }

    return createDefaultProject();
}
